import type { QueryKey } from "@tanstack/react-query";
import type {
  AudienceFilters,
  NavigatorFilters,
} from "../../pages/campaigns/types";
import type {
  EmailOutreachType,
  FilterOptions,
  LeadsFacets,
  LeadsSearchParams,
  MetricsParameters,
  ProfileSearchParams,
  RentedSubscription,
  SelectedFacetParam,
  Subscription,
} from "../types";

export default function getQueryKeys(workspaceId: string) {
  const queryKeys = {
    workspaceKeys: {
      base: [{ workspaceId }, "workspaces"],
      accounts: () => [...queryKeys.workspaceKeys.base, "accounts"],
    },
    leadsKeys: {
      base: [{ workspaceId }, "leads"],
      listInfo: (searchParams: LeadsSearchParams<SelectedFacetParam>) => {
        return [...queryKeys.leadsKeys.base, "listInfo", searchParams];
      },
      list: (
        searchParams?: LeadsSearchParams<SelectedFacetParam>,
        pagingParams?: object,
      ) => {
        let key: QueryKey = [...queryKeys.leadsKeys.base, "list"];
        if (searchParams) {
          key = [...key, searchParams];
        }
        if (pagingParams) {
          key = [...key, pagingParams];
        }
        return key;
      },
      details: (leadId?: string) => {
        const base = [...queryKeys.leadsKeys.base, "details"];
        return leadId ? [...base, leadId] : base;
      },
      recent: (accountIds?: string[], campaignId?: string, flowId?: string) => {
        const base = [{ workspaceId }, "recent"];
        if (accountIds) return [...base, accountIds];
        if (campaignId) return [...base, campaignId];
        if (flowId) return [...base, flowId];
        return base;
      },
      conversationUrn: (leadId: string) => [
        ...queryKeys.leadsKeys.base,
        "conversationUrn",
        leadId,
      ],
      facet: (
        facetName: keyof LeadsFacets,
        searchParams?: LeadsSearchParams<SelectedFacetParam>,
      ) => {
        let key: QueryKey = [...queryKeys.leadsKeys.base, "facet", facetName];
        if (searchParams) {
          key = [...key, searchParams];
        }
        return key;
      },
    },
    campaignsKeys: {
      base: [{ workspaceId }, "campaigns"],
      list: () => [...queryKeys.campaignsKeys.base, "list"],
      details: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "details",
        campaignId,
      ],
      completion: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "completion",
        campaignId,
      ],
      metrics: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "metrics",
        campaignId,
      ],
      flowMetrics: (campaignId: string, flowId: string) => [
        ...queryKeys.campaignsKeys.base,
        "flowMetrics",
        campaignId,
        flowId,
      ],
      targeting: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "targeting",
        campaignId,
      ],
      audience: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "audience",
        campaignId,
      ],
      audienceSearch: (campaignId: string, filters: AudienceFilters) => [
        ...queryKeys.campaignsKeys.base,
        "audienceSearch",
        campaignId,
        filters,
      ],
      audienceStats: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "audienceStats",
        campaignId,
      ],
      audienceToken: (campaignId: string) => [
        ...queryKeys.campaignsKeys.base,
        "audienceToken",
        campaignId,
      ],
      profilesPreview: (
        campaignId: string,
        searchParams: Partial<ProfileSearchParams>,
      ) => [
        ...queryKeys.campaignsKeys.base,
        "profilesPreview",
        campaignId,
        searchParams,
      ],
      linkedInGroups: (campaignId: string, miner: string) => [
        ...queryKeys.campaignsKeys.base,
        "linkedInGroups",
        campaignId,
        miner,
      ],
      linkedInGroup: (campaignId: string, groupId: string) => [
        ...queryKeys.campaignsKeys.base,
        "linkedInGroup",
        campaignId,
        groupId,
      ],
      linkedInEvents: (campaignId: string, miner: string) => [
        ...queryKeys.campaignsKeys.base,
        "linkedInEvents",
        campaignId,
        miner,
      ],
      linkedInEvent: (campaignId: string, eventId: string) => [
        ...queryKeys.campaignsKeys.base,
        "linkedInEvent",
        campaignId,
        eventId,
      ],
      crmLeads: (
        campaignId: string,
        sourceId: string,
        type: EmailOutreachType,
      ) => [
        ...queryKeys.campaignsKeys.base,
        "crmLeads",
        campaignId,
        sourceId,
        type,
      ],
      navigatorLeads: (
        campaignId: string,
        keywords: string,
        filters: NavigatorFilters,
      ) => [
        ...queryKeys.campaignsKeys.base,
        "navigatorLeads",
        campaignId,
        keywords,
        filters,
      ],
      navigatorTypeahead: (campaignId: string, type: string, query: string) => [
        ...queryKeys.campaignsKeys.base,
        "navigatorTypeahead",
        campaignId,
        type,
        query,
      ],
      count: () => [...queryKeys.campaignsKeys.base, "count"],
    },
    conversationsKeys: {
      base: [{ workspaceId }, "conversations"],
      list: (
        keywords?: string,
        filters?: string[],
        filteredBy?: FilterOptions,
      ) => {
        const base = [...queryKeys.conversationsKeys.base, "list"];
        return [
          ...base,
          ...(keywords ? [keywords] : []),
          ...(filters || []),
          ...(filteredBy ? [filteredBy] : []),
        ];
      },
      messages: (conversationUrn: string) => [
        ...queryKeys.conversationsKeys.base,
        "messages",
        conversationUrn,
      ],
    },
    labelsKeys: {
      list: (type: string) => [{ workspaceId }, "labels", type],
    },
    templatesKeys: {
      list: [{ workspaceId }, "templates"],
    },
    blacklistKeys: {
      list: (type: "companies" | "accounts") => [
        { workspaceId },
        "blacklist",
        type,
      ],
    },
    companiesSearchKeys: {
      list: (keyword: string) => [{ workspaceId }, "companiesSearch", keyword],
    },
    profileSearchKeys: {
      list: (keyword: string) => [{ workspaceId }, "profilesSearch", keyword],
    },
    metricsKeys: {
      list: (
        selectedTimeframe: MetricsParameters | string,
        accountIds: string[],
        campaignId?: string,
        flowId?: string,
      ) => {
        const base = [
          { workspaceId },
          "metrics",
          selectedTimeframe,
          accountIds,
        ];
        if (campaignId) return [...base, campaignId];
        if (flowId) return [...base, flowId];
        return base;
      },
    },
    paymentSourceKeys: {
      list: ["paymentSources"],
      primary: (subscriptionId: string) => [
        { workspaceId, subscriptionId },
        "primaryPaymentSource",
      ],
    },
    membersKeys: {
      list: [{ workspaceId }, "members"],
    },
    agencyClientsKeys: {
      list: [{ workspaceId }, "agencyClients"],
    },
    invoicesKeys: {
      list: [{ workspaceId }, "invoices"],
    },
    webhooksKeys: {
      list: [{ workspaceId }, "webhooks"],
    },
    proxiesKeys: {
      list: [{ workspaceId }, "proxies"],
    },
    brandSettingsKey: [{ workspaceId }, "brandSettings"],
    agencySettingsKey: [{ workspaceId }, "agencySettingsKey"],
    agency: {
      base: ["agency"],
      invoices: () => [...queryKeys.agency.base, "invoices"],
    },
    integrationSettingsKey: [{ workspaceId }, "integrationSettings"],
    accountsKeys: {
      proxy: (accountId: string) => ["proxy", accountId],
      limits: (accountId: string) => ["limits", accountId],
      ssi: (accountId: string) => ["ssi", accountId],
      salesNavigatorContract: (accountId: string) => [
        "salesNavigatorContract",
        accountId,
      ],
      salesNavigatorContracts: (accountId: string) => [
        "salesNavigatorContracts",
        accountId,
      ],
    },
    estimate: {
      renew: (subscription: Subscription | RentedSubscription) => [
        "estimateRenewal",
        workspaceId,
        ...(subscription ? [subscription.id, subscription.status] : []),
      ],
    },
  };

  return queryKeys;
}
